import {
    css,
    IS_DEFINED,
    IS_NOT,
    nil,
    NO,
    UIAutocompleteDropdownView,
    UIAutocompleteItem,
    UIAutocompleteTextField,
    UIComponentView,
    UITextField,
    UIView,
    YES
} from "uicore-ts"
import { RHAutocompleteDropdownView } from "./RHAutocompleteDropdownView"
import { RHColor } from "./RHColor"
import { RHForms } from "./Utils_slop/RHDesignTokens"


@UIComponentView
export class RHAutocompleteTextField<T = string> extends UIAutocompleteTextField<T> {

    // --- Validation Configuration (mirrors RHTextField) ---

    isRequired = NO
    validationRegex?: RegExp
    validateOnInput = NO
    validateOnBlur = YES
    protected hasBeenBlurred = NO
    _hasValidated = NO

    validatesAgainstAutocomplete = NO

    private readonly defaultBorderColor = RHForms.textFieldBorderColor.stringValue

    autocompleteDisplayLabelForItem?: (item: UIAutocompleteItem<T>) => string

    /**
     * Called after the user presses Enter and that press causes an item to
     * be committed into the field (i.e. the highlighted dropdown row is
     * entered as the selection). Not called when Enter merely closes an
     * already-empty-highlight dropdown, and not called for selections made
     * by clicking a row or by Tab.
     *
     * The default behaviour blurs the field. Replace this to move focus
     * elsewhere instead — for example to the next or previous item in
     * `RHPageFocusManager`:
     *
     * ```typescript
     * field.didCommitSelectionViaEnter = () => {
     *     RHPageFocusManager.shared.focusNext()
     * }
     * ```
     */
    didCommitSelectionViaEnter: () => void = () => this.blur()

    /**
     * Set by the overridden `commitSelection` below whenever it actually
     * runs. Read (and cleared) by our own EnterDown listener, registered
     * after the base class's EnterDown listener, to determine whether this
     * specific Enter press resulted in a real commit.
     */
    private didCommitSelectionDuringCurrentDispatch = NO
    _isClosingDropdown = NO
    _dropdownDismissalRootView?: UIView
    _dropdownDismissalPointerDown = (_sender: UIView, event: Event) => {

        if (
            event.target instanceof Node &&
            !this.viewHTMLElement.contains(event.target) &&
            !this._dropdownView.viewHTMLElement.contains(event.target)
        ) {
            this.closeDropdown()
        }

    }


    constructor(elementID?: string) {

        super(elementID)

        this.viewHTMLElement.classList.add(this.styleClassName);

        // Bootstrap styling matching RHTextField
        (this as RHAutocompleteTextField<T>).configureWithObject({
            textElementView: {
                style: {
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    padding: "0",
                    margin: "0",
                    boxShadow: "none",
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    lineHeight: "inherit",
                    color: "inherit"
                }
            },
            textColor: RHColor.textBlack,
            backgroundColor: RHColor.textWhite,
            style: {
                display: "flex",
                alignItems: "center",
                width: "100%",
                padding: RHForms.textFieldContentPadding,
                fontFamily: RHForms.textFieldFontFamily,
                fontSize: RHForms.textFieldFontSize,
                fontWeight: RHForms.textFieldFontWeight,
                lineHeight: RHForms.textFieldLineHeight,
                backgroundClip: "padding-box",
                border: `1px solid ${this.defaultBorderColor}`,
                borderRadius: RHForms.textFieldBorderRadius,
                transition: RHForms.textFieldTransition,
                boxSizing: "border-box",
                outline: "none",
                appearance: "none"
            }
        })

        // Focus visual state
        this.addTargetForControlEvent(UIView.controlEvent.Focus, () => {
            this.style.borderColor = RHColor.primaryBorderHover.stringValue
            this.style.boxShadow = `0 0 0 0.25rem ${RHColor.primaryShadow.stringValue}`
            this.style.outline = "0"
        })

        // Blur visual state
        this.addTargetForControlEvent(UIView.controlEvent.Blur, (sender, event) => {
            const focusEvent = event as FocusEvent
            if (
                focusEvent.relatedTarget instanceof Node &&
                this._dropdownView.viewHTMLElement.contains(focusEvent.relatedTarget)
            ) {
                return
            }
            this.hasBeenBlurred = YES
            this.style.borderColor = this.defaultBorderColor
            this.style.boxShadow = "none"
            if (this.validateOnBlur) {
                this.validate()
            }
        })

        // Validate on text change if configured
        this.addTargetForControlEvent(UITextField.controlEvent.TextChange, () => {
            if (this.style.borderColor === RHColor.danger.stringValue) {
                this.validate()
            }
            else if (this.validateOnInput && this.hasBeenBlurred) {
                this.validate()
            }
        })

        // Placeholder styling
        if (this.elementID) {
            UIView.injectCSS(css`
                #${this.elementID}_textElement::placeholder {
                    color: ${RHForms.textFieldPlaceholderColor};
                    opacity: 1;
                    user-select: none;
                }
            `, `rh-autocomplete-style-${this.elementID}`)
        }

        // The base class's own EnterDown handler (registered earlier, inside
        // super()) runs first and — when a row is highlighted — calls
        // commitSelection() synchronously before this listener executes.
        // Listeners for the same dispatched event run synchronously, in
        // registration order, within the same call stack. So by the time
        // this callback runs, any Enter-triggered commit has already
        // completed, and the overridden commitSelection below will already
        // have set didCommitSelectionDuringCurrentDispatch.
        this.addTargetForControlEvent(UIView.controlEvent.EnterDown, () => {
            if (this.didCommitSelectionDuringCurrentDispatch) {
                this.didCommitSelectionDuringCurrentDispatch = NO
                this.didCommitSelectionViaEnter()
            }
        })

    }


    override newDropdownView(): UIAutocompleteDropdownView<T> {
        return new RHAutocompleteDropdownView<T>(
            this.elementID ? this.elementID + "Dropdown" : undefined
        ).configuredWithObject({
            displayLabelForItem: item => {
                if (this.autocompleteDisplayLabelForItem) {
                    return this.autocompleteDisplayLabelForItem(item)
                }
                return item.label
            }
        })
    }


    refreshAutocompleteDisplay() {
        const dropdownView = this._dropdownView as RHAutocompleteDropdownView<T>
        dropdownView.refreshVisibleRows()
    }


    override openDropdown() {

        if (this._isClosingDropdown) {
            return
        }

        super.openDropdown()

        // The base class resets the suggestions when a text change reopens the dropdown.
        if (this._userHasNavigatedDropdown && IS_NOT(this._selectedItem)) {
            this.updateFilteredItems()
        }

        const rootView = this.rootView
        if (this._dropdownDismissalRootView !== rootView) {
            this._dropdownDismissalRootView?.removeTargetForControlEvent(
                UIView.controlEvent.PointerDown,
                this._dropdownDismissalPointerDown
            )
            this._dropdownDismissalRootView = rootView
            rootView.addTargetForControlEvent(
                UIView.controlEvent.PointerDown,
                this._dropdownDismissalPointerDown
            )
        }

    }


    override closeDropdown() {

        this._isClosingDropdown = YES
        try {
            this._dropdownDismissalRootView?.removeTargetForControlEvent(
                UIView.controlEvent.PointerDown,
                this._dropdownDismissalPointerDown
            )
            this._dropdownDismissalRootView = undefined
            super.closeDropdown()
            this._dropdownView.removeFromSuperview()
        }
        finally {
            this._isClosingDropdown = NO
        }

    }


    override wasRemovedFromViewTree() {

        super.wasRemovedFromViewTree()
        this.closeDropdown()

    }


    /**
     * The base class commits `itemBeforeFocus` on Escape when strictSelection
     * is on — but that is undefined when the field had no selection before
     * focusing, and `item.label` then throws. Treat an undefined item as
     * "restore to no selection", matching the pre-focus state.
     *
     * Also records that a commit occurred (read by the EnterDown listener
     * registered above to invoke `didCommitSelectionViaEnter`).
     */
    override commitSelection(item: UIAutocompleteItem<T>) {

        this.didCommitSelectionDuringCurrentDispatch = YES
        // Defensive cleanup: if this call did not originate from the EnterDown
        // listener below (e.g. a row click or Tab), nothing will consume the
        // flag this tick, so clear it once the current synchronous dispatch
        // finishes to prevent it from leaking into a later, unrelated Enter
        // press that has no highlighted item.
        queueMicrotask(() => {
            this.didCommitSelectionDuringCurrentDispatch = NO
        })

        if (IS_NOT(item)) {
            this._selectedItem = undefined
            this.text = ""
            this.closeDropdown()
            this.updateValidationVisuals()
            this.sendControlEventForKey(
                UIAutocompleteTextField.controlEvent.SelectionDidChange,
                nil
            )
            return
        }

        super.commitSelection(item)
        this.closeDropdown()

    }


    // MARK: - Validation

    validate(): boolean {
        this._hasValidated = YES
        const currentValue = this.text ?? ""
        let isFieldValid = YES

        const hasValidationRules = this.isRequired ||
            IS_DEFINED(this.validationRegex) ||
            this.validatesAgainstAutocomplete

        if (!hasValidationRules) {
            this.resetValidationVisuals()
            return YES
        }

        // Required check
        if (this.isRequired && currentValue.length === 0) {
            isFieldValid = NO
        }

        // Regex check
        if (isFieldValid && currentValue.length > 0 && this.validationRegex) {
            if (!this.validationRegex.test(currentValue)) {
                isFieldValid = NO
            }
        }

        // Autocomplete validation: text must match an item label
        if (isFieldValid && this.validatesAgainstAutocomplete && currentValue.length > 0) {
            const matchesItem = this._autocompleteItems.some(
                item => item.label === currentValue
            )
            if (!matchesItem) {
                isFieldValid = NO
            }
        }

        this._applyValidationVisuals(isFieldValid)
        return isFieldValid

    }

    resetValidationVisuals() {
        this.style.borderColor = this.defaultBorderColor
        this.style.boxShadow = "none"
    }

    _applyValidationVisuals(isFieldValid: boolean) {
        if (isFieldValid) {
            this.style.borderColor = RHColor.success.stringValue
            this.style.boxShadow = "none"
        }
        else {
            this.style.borderColor = RHColor.danger.stringValue
            this.style.boxShadow = "none"
        }
    }


    /** Called by UIAutocompleteTextField after dropdown closes and after selection changes. */
    override updateValidationVisuals() {

        if (!this.validatesAgainstAutocomplete) {
            return
        }

        const currentText = this.text.trim()

        if (currentText.length === 0) {
            this.resetValidationVisuals()
            return
        }

        const matchesItem = this._autocompleteItems.some(
            item => item.label === currentText
        )

        this._applyValidationVisuals(matchesItem)

    }


    // MARK: - Enabled State

    override updateContentForCurrentEnabledState() {
        // UIView couples enabled to hidden; field visibility belongs to the form.
        if (this.enabled) {
            this.alpha = 1
            this.textColor = RHColor.textBlack
            this.backgroundColor = RHColor.textWhite
            this.textElementView.backgroundColor = this.backgroundColor
            this.style.cursor = "text"
            if (this._hasValidated || this.hasBeenBlurred) {
                this.validate()
            }
            else {
                this.resetValidationVisuals()
            }
        }
        else {
            this.alpha = 1
            this.textColor = RHColor.secondary
            this.backgroundColor = RHColor.disabled
            this.textElementView.backgroundColor = this.backgroundColor
            this.blur()
            this.style.borderColor = this.defaultBorderColor
            this.style.boxShadow = "none"
            this.style.cursor = "not-allowed"
        }

        this.userInteractionEnabled = this.enabled

    }


    // MARK: - Intrinsic Content Height

    override intrinsicContentHeight(constrainingWidth = 0) {

        if (this.viewHTMLElement.isConnected) {
            this.viewHTMLElement.offsetHeight
        }

        const textElementHeight = this.textElementView.viewHTMLElement.scrollHeight ||
            parseFloat(window.getComputedStyle(this.textElementView.viewHTMLElement).lineHeight) ||
            16

        const computed = window.getComputedStyle(this.viewHTMLElement)
        const paddingTop = parseFloat(computed.paddingTop) || 0
        const paddingBottom = parseFloat(computed.paddingBottom) || 0
        const borderTop = parseFloat(computed.borderTopWidth) || 0
        const borderBottom = parseFloat(computed.borderBottomWidth) || 0

        return textElementHeight + paddingTop + paddingBottom + borderTop + borderBottom

    }


}
